// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the commands in a shell code block of an agent reply, for Run in terminal (AgentCodeRun):
// which languages are shell, where each command starts and ends (a line, a line continued with `\`,
// a heredoc), and which commands wait for the person's Return instead of running at once.

/** Fences that hold commands for a POSIX shell (Konvo's terminals run the machine's login shell). */
const SHELLS = new Set(['bash', 'sh', 'shell', 'zsh', 'console', 'shellsession', 'sh-session', 'terminal']);
/** Fences whose commands carry a prompt (`$ `); the other lines are output. */
const SESSIONS = new Set(['console', 'shellsession', 'sh-session', 'terminal']);

export const isShellLanguage = (language: string) => SHELLS.has(language.toLowerCase());

export interface ShellCommand {
	/** The block's lines it covers (0-based, inclusive). */
	start: number;
	end: number;
	/** As typed: its lines joined by newlines, without a prompt. */
	text: string;
}

const PROMPT = /^\s*[$%]\s+/;
const HEREDOC = /<<-?\s*(['"]?)([A-Za-z_][\w-]*)\1/;

/** The commands in a block, in order; comments, blank lines and (in a session) output are left out. */
export function shellCommands(content: string, language: string): Array<ShellCommand> {
	const lines = content.replace(/\r\n?/g, '\n').replace(/\n$/, '').split('\n');
	const session = SESSIONS.has(language.toLowerCase());
	const commands: Array<ShellCommand> = [];
	for (let i = 0; i < lines.length; i++) {
		const raw = lines[i]!;
		if (session && !PROMPT.test(raw)) continue;
		const first = raw.replace(PROMPT, '');
		if (!first.trim() || first.trim().startsWith('#')) continue;
		const start = i;
		const parts = [first];
		let line = first;
		// Continued with a backslash: the next line is the same command (in a session, after a `> ` prompt).
		while (/\\\s*$/.test(line) && i + 1 < lines.length) {
			line = lines[++i]!.replace(session ? /^\s*>\s?/ : /^$/, '');
			parts.push(line);
		}
		// A heredoc runs to its closing word.
		const heredoc = HEREDOC.exec(parts.join('\n'));
		if (heredoc) {
			const word = heredoc[2]!;
			while (i + 1 < lines.length) {
				const next = lines[++i]!.replace(session ? /^\s*>\s?/ : /^$/, '');
				parts.push(next);
				if (next.trim() === word) break;
			}
		}
		commands.push({start, end: i, text: parts.join('\n')});
	}
	return commands;
}

/**
 * Commands that remove, overwrite, publish or run as root: typed into the terminal for the person to
 * read and press Return, never run at once.
 */
const RISKY = [
	/(^|[\s;&|(])(sudo|doas|su|rm|rmdir|shred|dd|mkfs(\.\w+)?|fdisk|parted|shutdown|reboot|halt|poweroff|kill|pkill|killall)(\s|$)/,
	/\bgit\s+(push|reset\s+--hard|clean\b|checkout\s+--\s|restore\b|branch\s+-D|stash\s+(drop|clear)|rebase|filter-branch)/,
	/\b(npm|pnpm|yarn)\s+publish\b/,
	/\b(drop|truncate)\s+(table|database|schema)\b/i,
	/\b(terraform|tofu)\s+(apply|destroy)\b/,
	/\b(kubectl|helm)\s+(delete|uninstall)\b/,
	/\bdocker\s+(rm|rmi|system\s+prune|volume\s+(rm|prune))\b/,
	/\|\s*(sudo\s+)?(sh|bash|zsh)\b/,
	/\bchmod\s+-R\b|\bchown\s+-R\b/,
	/(^|[^>&0-9])>\s*\/(etc|usr|bin|boot|dev)\//,
];

export const isRisky = (text: string) => RISKY.some((pattern) => pattern.test(text));

/** Bracketed paste: the shell takes several lines as typed text, run only on Return. */
const PASTE_START = '\u001b[200~';
const PASTE_END = '\u001b[201~';

/**
 * What goes to the terminal: the commands typed one after another with Return, or (when one is risky)
 * typed in and left for the person's Return.
 */
export function terminalInput(text: string): {data: string; waits: boolean} {
	const typed = text.replace(/\n/g, '\r');
	if (!isRisky(text)) return {data: `${typed}\r`, waits: false};
	return {data: text.includes('\n') ? `${PASTE_START}${typed}${PASTE_END}` : typed, waits: true};
}
